import ELK, {ElkEdgeSection, ElkExtendedEdge, ELK as ELKInterface} from 'elkjs/lib/elk.bundled';
import {Edge, Node, NodeOrigin} from 'reactflow';

import {assert} from 'Asserts';
import {Point} from 'util/geometry/GeometryConstants';

import {EdgeType} from '../edges/edgeOptions';
import {FlowchartData} from './algorithms';
import {DimensionsForLayout} from './calculateDimensionsForLayout';
import {unhideFlowchartElementAfterLayout} from './hideFlowchartElementBeforeLayoutUtils';
import {snapPositionToGrid} from './snapPositionToGrid';
import translateNodeOrigin from './translateNodeOrigin';

// ELK lays out nodes by the top-left of each node.
const ELK_NODE_ORIGIN: NodeOrigin = [0, 0];

// Controls how nodes behave during ELK layout.
const ELK_NODE_PROPERTIES = {
  // Handles are specified using their x/y positions relative to the node.
  'org.eclipse.elk.portConstraints': 'FIXED_POS',
};

const ELK_LAYOUT_OPTIONS = {
  'elk.algorithm': 'layered',
  // Left to right layout
  'elk.direction': 'RIGHT',
  // Create curved edges (splines)
  'elk.edgeRouting': 'SPLINES',
  // Allow edges to look more orthogonal to avoid nodes.
  'elk.layered.edgeRouting.splines.mode': 'CONSERVATIVE',
  // Don't merge edges.
  'elk.layered.mergeEdges': 'false',
  // keep some space between edges.
  'elk.spacing.edgeEdge': '20',
  // keep some space between edges between layers.
  'elk.layered.spacing.edgeEdgeBetweenLayers': '20',
  // Process connected components together so we can more easily enforce ordering when the graph is disconnected.
  'elk.separateConnectedComponents': 'false',
  // Place edge labels inline on the edge (hint that might not always be respected).
  'elk.edgeLabels.inline': 'true',
  // Place edge labels at the center of the edge (hint that might not always be respected).
  'elk.edgeLabels.placement': 'CENTER',
};

let elk: ELKInterface;

function validateStartAndEndNodes<N extends Node>(
  nodes: Array<N>,
  startNodeId?: string,
  endNodeId?: string
): void {
  assert(
    startNodeId == null || endNodeId == null || startNodeId !== endNodeId,
    'Start and end node IDs cannot be the same'
  );
  assert(
    startNodeId == null || nodes.find((node) => node.id === startNodeId) != null,
    'Start node ID does not exist'
  );
  assert(
    endNodeId == null || nodes.find((node) => node.id === endNodeId) != null,
    'End node ID does not exist'
  );
}

function maybeGetStartNodeOrderingProperty(testNodeId: string, startNodeId?: string): Record<string, string> {
  return startNodeId != null && startNodeId === testNodeId
    ? {
        'org.eclipse.elk.layered.layering.layerConstraint': 'FIRST_SEPARATE',
      }
    : {};
}

function maybeGetEndNodeOrderingProperty(testNodeId: string, endNodeId?: string): Record<string, string> {
  return endNodeId != null && endNodeId === testNodeId
    ? {
        'org.eclipse.elk.layered.layering.layerConstraint': 'LAST_SEPARATE',
      }
    : {};
}

function adjustPoint(point: Point, adjustment: {xAdjustment: number; yAdjustment: number}): Point {
  return {
    x: point.x + adjustment.xAdjustment,
    y: point.y + adjustment.yAdjustment,
  };
}

function adjustEdgeWithOneSectionForSnapToGrid(
  edge: Edge,
  edgeSections: Array<ElkEdgeSection>,
  snapAdjustmentsByNodeId: Record<string, {xAdjustment: number; yAdjustment: number}>
): Array<ElkEdgeSection> {
  assert(edgeSections.length === 1, 'Edge should have exactly one section');
  const [edgeSection] = edgeSections;
  // Adjust the start and endpoints of the edge to match the snap adjustments on the source and target nodes.
  edgeSection.startPoint = adjustPoint(edgeSection.startPoint, snapAdjustmentsByNodeId[edge.source]);
  edgeSection.endPoint = adjustPoint(edgeSection.endPoint, snapAdjustmentsByNodeId[edge.target]);

  // Adjust the edge bend points to match the snap adjustments on the source and target nodes. Bend points
  // closer to the source node are adjusted by the source node's snap adjustments, and bend points
  // closer to the target node are adjusted by the target node's snap adjustments.
  const bendPoints = edgeSection.bendPoints ?? [];
  const midIndex = Math.floor(bendPoints.length / 2);
  edgeSection.bendPoints = bendPoints.map((bendpoint, idx) => {
    if (idx < midIndex) {
      return adjustPoint(bendpoint, snapAdjustmentsByNodeId[edge.source]);
    }
    return adjustPoint(bendpoint, snapAdjustmentsByNodeId[edge.target]);
  });

  return edgeSections;
}

function adjustEdgeWithMultipleSectionsForSnapToGrid(
  edge: Edge,
  edgeSections: Array<ElkEdgeSection>,
  snapAdjustmentsByNodeId: Record<string, {xAdjustment: number; yAdjustment: number}>
): Array<ElkEdgeSection> {
  const [firstSectionIdx, lastSectionIdx] = [0, edgeSections.length - 1];
  // Adjust the first and last edge sections by the snap adjustments of the edge's source and target nodes,
  // respectively. We'll leave any middle sections alone for now.
  edgeSections[firstSectionIdx].startPoint = adjustPoint(
    edgeSections[firstSectionIdx].startPoint,
    snapAdjustmentsByNodeId[edge.source]
  );
  edgeSections[firstSectionIdx].endPoint = adjustPoint(
    edgeSections[firstSectionIdx].endPoint,
    snapAdjustmentsByNodeId[edge.source]
  );
  edgeSections[firstSectionIdx].bendPoints = edgeSections[firstSectionIdx].bendPoints?.map((bendPoint) =>
    adjustPoint(bendPoint, snapAdjustmentsByNodeId[edge.source])
  );

  edgeSections[lastSectionIdx].startPoint = adjustPoint(
    edgeSections[lastSectionIdx].startPoint,
    snapAdjustmentsByNodeId[edge.target]
  );
  edgeSections[lastSectionIdx].endPoint = adjustPoint(
    edgeSections[lastSectionIdx].endPoint,
    snapAdjustmentsByNodeId[edge.target]
  );
  edgeSections[lastSectionIdx].bendPoints = edgeSections[lastSectionIdx].bendPoints?.map((bendPoint) =>
    adjustPoint(bendPoint, snapAdjustmentsByNodeId[edge.target])
  );
  return edgeSections;
}

function adjustEdgeForSnapToGrid(
  edge: Edge,
  edgeSections: Array<ElkEdgeSection>,
  snapAdjustmentsByNodeId: Record<string, {xAdjustment: number; yAdjustment: number}>
): Array<ElkEdgeSection> {
  if (edgeSections.length === 1) {
    return adjustEdgeWithOneSectionForSnapToGrid(edge, edgeSections, snapAdjustmentsByNodeId);
  }
  return adjustEdgeWithMultipleSectionsForSnapToGrid(edge, edgeSections, snapAdjustmentsByNodeId);
}

const elkLayout = async <N extends Node, E extends Edge>(
  {nodes, edges, getHandles}: FlowchartData<N, E>,
  dimensions: DimensionsForLayout
): Promise<FlowchartData<N, E>> => {
  const allHandleIds: Array<string> = [];
  const {startNodeId, endNodeId} = dimensions;
  validateStartAndEndNodes(nodes, startNodeId, endNodeId);

  // Convert nodes and edges to ELK format
  const elkNodes = nodes.map((node) => {
    assert(dimensions.nodes[node.id] != null, 'Node is missing dimensions needed for layout');
    const {height, width} = dimensions.nodes[node.id];
    const handles = getHandles?.(node.id) ?? [];
    allHandleIds.push(...handles.map((handle) => handle.id ?? ''));

    const elkStartNodeProperty = maybeGetStartNodeOrderingProperty(node.id, startNodeId);
    const elkEndNodeProperty = maybeGetEndNodeOrderingProperty(node.id, endNodeId);

    return {
      id: node.id,
      width,
      height,
      properties: {
        ...ELK_NODE_PROPERTIES,
        ...elkStartNodeProperty,
        ...elkEndNodeProperty,
      },
      ports: handles.map((handle) => ({
        id: handle.id ?? '',
        x: handle.x,
        y: handle.y,
        width: handle.width,
        height: handle.height,
      })),
    };
  });

  const elkEdges = edges.map((edge) => {
    assert(dimensions.edges[edge.id] != null, 'Edge is missing dimensions needed for layout');

    // Only validate handles if they are specified
    if (edge.sourceHandle != null) {
      assert(
        allHandleIds.includes(edge.sourceHandle),
        `Source handle ${edge.sourceHandle} for edge ${edge.id} not found on node.`
      );
    }
    if (edge.targetHandle != null) {
      assert(
        allHandleIds.includes(edge.targetHandle),
        `Target handle ${edge.targetHandle} for edge ${edge.id} not found on node.`
      );
    }

    return {
      id: edge.id,
      sources: [edge.sourceHandle ?? edge.source],
      targets: [edge.targetHandle ?? edge.target],
      labels:
        edge.label != null && typeof edge.label === 'string'
          ? [
              {
                text: edge.label,
                width: dimensions.edges[edge.id].width,
                height: dimensions.edges[edge.id].height,
              },
            ]
          : [],
    };
  });

  // Configure ELK layout
  const elkGraph = {
    id: 'root',
    children: elkNodes,
    edges: elkEdges,
    layoutOptions: {
      ...ELK_LAYOUT_OPTIONS,
      'elk.spacing.nodeNode': String(dimensions.verticalSpaceBetweenNodes),
      'elk.layered.spacing.nodeNodeBetweenLayers': String(dimensions.horizontalSpaceBetweenNodes),
      'elk.layered.spacing.edgeNodeBetweenLayers': String(dimensions.horizontalSpaceBetweenNodes),
    },
  };

  // Run ELK layout
  elk ??= new ELK();
  const layoutedGraph = await elk.layout(elkGraph);

  // Apply calculated positions, and un-hide any elements that were temporarily hidden.
  const elkNodesById = new Map(layoutedGraph.children?.map((child) => [child.id, child]) || []);
  const elkEdgesById = new Map(layoutedGraph.edges?.map((edge) => [edge.id, edge as ElkExtendedEdge]) || []);
  const snapAdjustmentsByNodeId: Record<string, {xAdjustment: number; yAdjustment: number}> = {};
  return {
    nodes: nodes.map((node) => {
      const elkNode = elkNodesById.get(node.id);
      assert(elkNode != null, `ELK layout did not return position for node ${node.id}`);

      const elkPosition = {x: elkNode.x ?? 0, y: elkNode.y ?? 0};
      let elkPositionX = elkPosition.x;
      let elkPositionY = elkPosition.y;
      if (dimensions.snapToGrid != null) {
        const snappedPosition = snapPositionToGrid(elkPosition, dimensions.snapToGrid);
        snapAdjustmentsByNodeId[node.id] = {
          xAdjustment: snappedPosition.x - elkPosition.x,
          yAdjustment: snappedPosition.y - elkPosition.y,
        };
        elkPositionX = snappedPosition.x;
        elkPositionY = snappedPosition.y;
      }
      return unhideFlowchartElementAfterLayout({
        ...node,
        position: translateNodeOrigin({
          sourceNodeOrigin: ELK_NODE_ORIGIN,
          sourcePosition: {x: elkPositionX, y: elkPositionY},
          targetNodeOrigin: dimensions.nodeOrigin,
          nodeHeight: node.height ?? 0,
          nodeWidth: node.width ?? 0,
        }),
      });
    }),
    edges: edges.map((edge) => {
      const elkEdge = elkEdgesById.get(edge.id);
      assert(
        elkEdge != null && elkEdge.sections != null && elkEdge.sections.length > 0,
        `ELK layout did not return edge data for edge ${edge.id}`
      );

      let elkEdgeSections = elkEdge.sections;
      if (dimensions.snapToGrid != null) {
        elkEdgeSections = adjustEdgeForSnapToGrid(edge, elkEdgeSections, snapAdjustmentsByNodeId);
      }
      return unhideFlowchartElementAfterLayout({
        ...edge,
        data: {
          ...edge.data,
          type: EdgeType.ELK_SPLINE,
          elkEdgeData: {
            sections: elkEdgeSections,
            labels: elkEdge.labels,
          },
        },
      });
    }),
  };
};

export default elkLayout;
